import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { openContactForm } from "../lib/contactModal";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: t.nav.about, key: "about", href: "#about" },
    { label: t.nav.services, key: "services", href: "#services" },
    { label: t.nav.work, key: "work", href: "#work" },
    { label: t.nav.clients, key: "clients", href: "#clients" },
  ];

  const handleClick = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const LangToggle = ({ testid }) => (
    <div
      data-testid={testid}
      className="flex items-center border border-[#262626] font-mono text-[11px] uppercase tracking-[0.2em]"
    >
      <button
        data-testid="lang-toggle-en"
        onClick={() => setLang("en")}
        className={`px-2.5 py-1.5 transition-colors ${
          lang === "en" ? "bg-[#ff4500] text-black" : "text-neutral-400 hover:text-white"
        }`}
        aria-label="Switch to English"
      >
        EN
      </button>
      <span className="w-px h-4 bg-[#262626]" />
      <button
        data-testid="lang-toggle-pt"
        onClick={() => setLang("pt")}
        className={`px-2.5 py-1.5 transition-colors ${
          lang === "pt" ? "bg-[#ff4500] text-black" : "text-neutral-400 hover:text-white"
        }`}
        aria-label="Mudar para Português"
      >
        PT
      </button>
    </div>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="logo-link"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2 group"
        >
          <span className="text-[#ff4500] font-mono text-sm">●</span>
          <span className="font-serif italic text-2xl tracking-tight group-hover:text-[#ff4500] transition-colors">
            tico<span className="text-[#ff4500]">.</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((l) => (
            <button
              key={l.key}
              data-testid={`nav-link-${l.key}`}
              onClick={() => handleClick(l.href)}
              className="link-underline text-[13px] uppercase tracking-[0.18em] text-neutral-300 hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LangToggle testid="lang-toggle-desktop" />
          <button
            type="button"
            onClick={openContactForm}
            data-testid="header-cta-button"
            className="btn-orange px-5 py-2.5 text-[12px] uppercase tracking-[0.2em] font-medium"
          >
            <span>{t.nav.letsTalk}</span>
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden absolute top-[72px] left-0 right-0 bg-[#0a0a0a] border-t border-white/5 px-6 py-8"
        >
          <div className="flex flex-col gap-6">
            {navLinks.map((l) => (
              <button
                key={l.key}
                data-testid={`mobile-nav-link-${l.key}`}
                onClick={() => handleClick(l.href)}
                className="text-left font-serif text-3xl text-white hover:text-[#ff4500] transition-colors"
              >
                {l.label}
              </button>
            ))}
            <div className="pt-4">
              <LangToggle testid="lang-toggle-mobile" />
            </div>
            <button
              type="button"
              onClick={() => { setMobileOpen(false); openContactForm(); }}
              data-testid="mobile-cta-button"
              className="inline-flex btn-orange px-5 py-3 text-[12px] uppercase tracking-[0.2em] font-medium mt-4 w-fit"
            >
              <span>{t.nav.letsTalk}</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
